import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { type MessageKey, t } from '../i18n';
import { useMe } from '../lib/auth';
import { can } from '../lib/permissions';
import { type IconName, ModuleIcon } from './icons';
import { NotificationsBell } from './NotificationsBell';
import { isPortalUser, PortalLayout } from './PortalLayout';
import { Alert } from './ui';
import { UserMenu } from './UserMenu';

// Módulos del menú (guía 1). Se habilitan a medida que se construyen. `also`: otras rutas que son del módulo.
const MODULES: Array<{ to: string; label: MessageKey; icon: IconName; ready: boolean; /** Con varios, alguno. */ permission?: string | string[]; also?: string[] }> = [
  { to: '/', label: 'menu.inicio', icon: 'inicio', ready: true },
  { to: '/tickets', label: 'menu.tickets', icon: 'tickets', ready: true },
  { to: '/proyectos', label: 'menu.proyectos', icon: 'proyectos', ready: true },
  { to: '/tareas', label: 'menu.tareas', icon: 'tareas', ready: true },
  { to: '/inventario', label: 'menu.inventario', icon: 'inventario', ready: true, permission: 'inventario.ver' },
  { to: '/monitoreo', label: 'menu.monitoreo', icon: 'monitoreo', ready: true, permission: ['monitoreo.ver', 'monitoreo.gestionar', 'monitoreo.avisos'] },
  { to: '/usuarios', label: 'menu.usuarios', icon: 'usuarios', ready: true, also: ['/licencias', '/proveedores'] },
  { to: '/conocimiento', label: 'menu.conocimiento', icon: 'conocimiento', ready: true },
  { to: '/kpis', label: 'menu.kpis', icon: 'kpis', ready: false },
  { to: '/parametros', label: 'menu.parametros', icon: 'parametros', ready: true, permission: 'plataforma.parametros.gestionar' },
];

// Administración: visible solo con el permiso (la API valida igual).
const ADMIN: Array<{ to: string; label: MessageKey; icon: IconName; permission: string; operator?: boolean }> = [
  { to: '/acceso/roles', label: 'menu.roles', icon: 'roles', permission: 'plataforma.roles.ver' },
  { to: '/acceso/usuarios', label: 'menu.asignaciones', icon: 'asignaciones', permission: 'plataforma.roles.ver' },
  { to: '/plataforma/organizaciones', label: 'menu.organizaciones', icon: 'organizaciones', permission: 'plataforma.tenants.gestionar' },
  // Otros tenants: solo desde el tenant operador de la plataforma.
  { to: '/plataforma/tenants', label: 'menu.tenants', icon: 'tenants', permission: 'plataforma.tenants.gestionar', operator: true },
];

/** Preferencia de cada persona en su navegador: el menú lateral colapsado (solo iconos). */
const COLLAPSED_KEY = 'sit365.menu.colapsado';
function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

// La sección activa lleva la señal ámbar a la izquierda.
const itemClass = (active: boolean, collapsed: boolean) =>
  `flex items-center gap-3 rounded-r py-2 text-sm transition-colors ${collapsed ? 'justify-center px-0' : 'px-4'} ${
    active ? 'bg-white/10 font-medium text-white shadow-[inset_3px_0_0_var(--color-senal)]' : 'text-white/75 hover:bg-white/5 hover:text-white'
  }`;

const inModule = (path: string, to: string) => path === to || path.startsWith(`${to}/`);

/** Quien no tiene ningún permiso (usuario final) ve el portal simple; el personal, el menú lateral con los módulos. */
export function Layout() {
  const me = useMe();
  return isPortalUser(me) ? <PortalLayout /> : <StaffLayout />;
}

function StaffLayout() {
  const me = useMe();
  const { pathname } = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const pendingRoles = [...new Set(me.rolesPendingMfa.map((r) => r.roleName))];
  // Un módulo listo que exige un permiso no se muestra a quien no lo tiene.
  const modules = MODULES.filter((m) => !m.permission || [m.permission].flat().some((p) => can(me, p)));
  const admin = ADMIN.filter((a) => can(me, a.permission) && (!a.operator || me.platformOperator));

  // En pantallas chicas, el menú se cierra al cambiar de página.
  useEffect(() => setNavOpen(false), [pathname]);

  function toggleCollapsed() {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
    } catch {
      // Sin almacenamiento (navegación privada): vale solo para esta visita.
    }
  }

  /** Menú de módulos. Colapsado muestra solo los iconos; el nombre queda para lectores de pantalla y como tooltip. */
  const nav = (compact: boolean) => {
    const label = (key: MessageKey) => <span className={compact ? 'sr-only' : ''}>{t(key)}</span>;
    return (
      <>
        <nav className="space-y-0.5 pr-3" aria-label="Módulos">
          {modules.map((m) =>
            m.ready ? (
              <NavLink
                key={m.to}
                to={m.to}
                end={m.to === '/'}
                title={compact ? t(m.label) : undefined}
                className={({ isActive }) => itemClass(isActive || (m.also ?? []).some((p) => inModule(pathname, p)), compact)}
              >
                <ModuleIcon name={m.icon} />
                {label(m.label)}
              </NavLink>
            ) : (
              <span
                key={m.to}
                className={`flex items-center gap-3 py-2 text-sm text-white/35 ${compact ? 'justify-center' : 'px-4'}`}
                title={compact ? `${t(m.label)} (${t('comun.proximamente')})` : t('comun.proximamente')}
              >
                <ModuleIcon name={m.icon} />
                {label(m.label)}
              </span>
            ),
          )}
        </nav>
        {admin.length > 0 && (
          <nav className="mt-6 space-y-0.5 pr-3" aria-label={t('menu.administracion')}>
            {compact ? <hr className="mx-4 mb-2 border-white/15" /> : <p className="px-4 pb-1.5 text-xs font-medium text-white/50">{t('menu.administracion')}</p>}
            {admin.map((a) => (
              <NavLink key={a.to} to={a.to} title={compact ? t(a.label) : undefined} className={({ isActive }) => itemClass(isActive, compact)}>
                <ModuleIcon name={a.icon} />
                {label(a.label)}
              </NavLink>
            ))}
          </nav>
        )}
      </>
    );
  };

  const brand = (
    <Link to="/" className="block min-w-0 rounded-sm px-4 py-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-senal">
      <span className="block text-lg font-semibold tracking-tight text-white">{t('app.nombre')}</span>
      <span className="block text-xs text-white/55">{t('app.area')}</span>
    </Link>
  );

  /** Contraer o expandir el menú: arriba, junto al nombre (contraído, ocupa su lugar). */
  const collapseButton = (
    <button
      type="button"
      className="rounded p-1.5 text-white/60 hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-senal"
      aria-label={collapsed ? t('menu.expandir') : t('menu.colapsar')}
      title={collapsed ? t('menu.expandir') : t('menu.colapsar')}
      aria-pressed={collapsed}
      onClick={toggleCollapsed}
    >
      <ModuleIcon name={collapsed ? 'expandir' : 'colapsar'} />
    </button>
  );

  return (
    <div className="flex min-h-full">
      <aside className={`hidden shrink-0 bg-tinta transition-[width] duration-150 md:block ${collapsed ? 'w-16' : 'w-60'}`}>
        <div className="sticky top-0 flex max-h-screen min-h-screen flex-col overflow-y-auto pb-4">
          {collapsed ? (
            <div className="flex justify-center py-5">{collapseButton}</div>
          ) : (
            <div className="flex items-start justify-between pr-2">
              {brand}
              <div className="pt-5">{collapseButton}</div>
            </div>
          )}
          <div className="flex-1">{nav(collapsed)}</div>
        </div>
      </aside>

      {navOpen && (
        <div className="fixed inset-0 z-30 md:hidden">
          <button type="button" className="absolute inset-0 bg-tinta/40" aria-label={t('menu.cerrar_menu')} onClick={() => setNavOpen(false)} />
          <aside id="menu-movil" className="relative h-full w-64 overflow-y-auto bg-tinta pb-6 shadow-xl">
            {brand}
            {nav(false)}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-2">
          <button
            type="button"
            className="rounded p-1.5 text-slate-700 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-marca/40 md:hidden"
            aria-label={t('menu.abrir_menu')}
            aria-expanded={navOpen}
            aria-controls="menu-movil"
            onClick={() => setNavOpen(true)}
          >
            <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
              <path d="M3 5h14M3 10h14M3 15h14" strokeLinecap="round" />
            </svg>
          </button>
          <span className="font-semibold text-tinta md:hidden">{t('app.nombre')}</span>
          <div className="ml-auto flex items-center gap-3">
            <NotificationsBell />
            <UserMenu />
          </div>
        </header>

        <main className="mx-auto w-full max-w-[90rem] flex-1 space-y-5 p-4 md:p-8">
          {pendingRoles.length > 0 && (
            <Alert tone="warning">
              {t('aviso.activar_2fa', { roles: pendingRoles.join(', ') })}{' '}
              <Link to="/perfil/seguridad" className="font-medium underline">
                {t('aviso.activar_2fa_accion')}
              </Link>
            </Alert>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}

